import React, { useRef, useState, useEffect } from "react";
import { TextField, InputAdornment, IconButton, TextFieldProps as MuiTextFieldProps, SxProps, Theme, Box, Typography, useTheme } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { primaryColor, stateSuccessColor } from "../config/constants";
import { validatePassword, PasswordValidation } from "../utils/customFunctions";

export interface BasicPasswordFieldProps extends Omit<MuiTextFieldProps, "type" | "onChange" | "value"> {
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    confirmRequired?: boolean;
    onPasswordValidated?: (password: string, isValid: boolean, validation: PasswordValidation) => void;
    showRequirements?: boolean;
}

export const BasicPasswordField: React.FC<BasicPasswordFieldProps> = ({
    confirmRequired = false,
    onPasswordValidated,
    showRequirements = true,
    onChange,
    ...props
}) => {
    const theme = useTheme();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>(
        validatePassword("")
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const confirmInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const validation = validatePassword(password, confirmRequired ? confirmPassword : undefined);
        setPasswordValidation(validation);

        // Call parent callback
        if (onPasswordValidated) {
            onPasswordValidated(password, validation.isValid, validation);
        }
    }, [password, confirmPassword, confirmRequired, onPasswordValidated]);

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = event.target.value;
        setPassword(newPassword);

        // Call original onChange if provided
        if (onChange) {
            onChange(event);
        }
    };

    const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newConfirmPassword = event.target.value;
        setConfirmPassword(newConfirmPassword);
    };

    const handleClickShowPassword = () => {
        const cursorPosition = inputRef.current?.selectionStart || 0;
        setShowPassword(!showPassword);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.selectionStart = cursorPosition;
                inputRef.current.selectionEnd = cursorPosition;
            }
        }, 0);
    };

    const handleClickShowConfirmPassword = () => {
        const cursorPosition = confirmInputRef.current?.selectionStart || 0;
        setShowConfirmPassword(!showConfirmPassword);
        setTimeout(() => {
            if (confirmInputRef.current) {
                confirmInputRef.current.selectionStart = cursorPosition;
                confirmInputRef.current.selectionEnd = cursorPosition;
            }
        }, 0);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    // Only apply custom autofill styling if primaryColor is not black
    const autofillStyles: SxProps<Theme> = primaryColor !== "#000000" ? {
        "& .MuiInputBase-input:-webkit-autofill": {
            "WebkitBoxShadow": `0 0 0 100px ${primaryColor}25 inset !important`,
            "WebkitTextFillColor": "inherit",
            caretColor: "inherit",
        }
    } : {};

    const passwordFieldSx = {
        "& .MuiInputLabel-root": {
            color: "grey",
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: "grey",
        },
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: "grey",
            },
            "&:hover fieldset": {
                borderColor: "grey",
            },
            "&.Mui-focused fieldset": {
                borderColor: "grey",
            },
        },
        "& .MuiInputBase-input": {
            letterSpacing: showPassword ? 1 : 6,
        },
        ...autofillStyles,
        ...(props.sx || {})
    };

    const confirmPasswordFieldSx = {
        "& .MuiInputLabel-root": {
            color: "grey",
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: "grey",
        },
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: "grey",
            },
            "&:hover fieldset": {
                borderColor: "grey",
            },
            "&.Mui-focused fieldset": {
                borderColor: "grey",
            },
        },
        "& .MuiInputBase-input": {
            letterSpacing: showConfirmPassword ? 1 : 6,
        },
        ...autofillStyles,
    };

    const RequirementItem = ({
        isValid,
        text
    }: {
        isValid: boolean;
        text: string;
    }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '1px' }}>
            {isValid ? (
                <Box
                    sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: stateSuccessColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: 'bold'
                    }}
                >
                    ✓
                </Box>
            ) : (
                <RadioButtonUncheckedIcon sx={{ color: theme.palette.text.disabled, fontSize: 16 }} />
            )}
            <Typography
                variant='body2'
                sx={{
                    color: isValid ? stateSuccessColor : theme.palette.text.secondary,
                    fontSize: '0.75rem'
                }}
            >
                {text}
            </Typography>
        </Box>
    );

    return (
        <>
            {/* Main Password Field */}
            <TextField
                {...props}
                id={props.id || 'password'}
                name={props.name || 'password'}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                inputRef={inputRef}
                InputProps={{
                    ...props.InputProps,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={passwordFieldSx}
            />

            {/* Confirm Password Field */}
            {confirmRequired && (
                <TextField
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    inputRef={confirmInputRef}
                    id={`${props.id || 'password'}-confirm`}
                    name={`${props.name || 'password'}-confirm`}
                    label="Confirm Password"
                    autoComplete="new-password"
                    InputProps={{
                        style: {
                            ...props.InputProps?.style,
                        },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle confirm password visibility"
                                    onClick={handleClickShowConfirmPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        ...confirmPasswordFieldSx,
                        marginTop: 2
                    }}
                />
            )}

            {/* Password Requirements */}
            {showRequirements && password && (
                <Box sx={{ mt: 2, mb: 2, ml: 1 }}>
                    <Typography
                        variant='body2' 
                        sx={{ 
                            mb: 1,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            fontSize: '0.85rem'
                        }}
                    >
                        Password Requirements:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <RequirementItem
                            isValid={passwordValidation.minLength}
                            text='At least 8 characters'
                        />
                        <RequirementItem
                            isValid={passwordValidation.hasUppercase}
                            text='One uppercase letter'
                        />
                        <RequirementItem
                            isValid={passwordValidation.hasLowercase}
                            text='One lowercase letter'
                        />
                        <RequirementItem
                            isValid={passwordValidation.hasNumber}
                            text='One number'
                        />
                        {confirmRequired && (
                            <RequirementItem
                                isValid={passwordValidation.passwordsMatch || false}
                                text='Passwords match' 
                            />
                        )}
                    </Box>
                </Box>
            )}
        </>
    );
};